// File Upload Constants
export const FILE_CONSTANTS = {
  // File size limits
  MAX_FILE_SIZE_BYTES: 50 * 1024 * 1024, // 50MB
  MAX_FILE_SIZE_MB: 50,

  // General accepted file types (fallback)
  ACCEPTED_MIME_TYPES: [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/csv",
  ] as const,
  ACCEPTED_EXTENSIONS: [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".csv"] as const,

  // Document type specific configurations
  DROPZONE_DOCUMENT: {
    CLAIMS: {
      ACCEPTED_MIME_TYPES: [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
      ] as const,
      ACCEPTED_EXTENSIONS: [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".csv"] as const,
      VALIDATION_MESSAGE: "Expects PDF, DOC, DOCX, XLS, XLSX, or CSV files for claims",
      MAX_FILES: 20,
      LABEL: 'Drag & drop your claim files or <span class="filepond--label-action">browse</span>',
    },
    POLICIES: {
      ACCEPTED_MIME_TYPES: [
        "application/pdf",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
      ] as const,
      ACCEPTED_EXTENSIONS: [".pdf", ".xls", ".xlsx", ".csv"] as const,
      VALIDATION_MESSAGE: "Expects PDF, XLS, XLSX, or CSV files for policies",
      MAX_FILES: 10,
      LABEL: 'Drag & drop your policy documents or <span class="filepond--label-action">browse</span>',
    },
    REIMBURSEMENTS: {
      ACCEPTED_MIME_TYPES: [
        "application/pdf",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
      ] as const,
      ACCEPTED_EXTENSIONS: [".pdf", ".xls", ".xlsx", ".csv"] as const,
      VALIDATION_MESSAGE: "Expects PDF, XLS, XLSX, or CSV files for reimbursements",
      MAX_FILES: 10,
      LABEL: 'Drag & drop your reimbursement documents or <span class="filepond--label-action">browse</span>',
    },
    // Storage-only reference attachments (#6506 / ADR-086). Deliberately broader
    // than the POLICIES list — "other documents": correspondence, EOBs, carrier
    // letters, signed forms, scans, emails — agreed with the client rather than
    // inheriting the policy list. Non-previewable types (Office, TIFF, email)
    // fall back to a download in AttachmentPreview (ADR-087 D4); PDF, images, and
    // text preview inline.
    ATTACHMENTS: {
      ACCEPTED_MIME_TYPES: [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
        "text/plain",
        "image/png",
        "image/jpeg",
        "image/gif",
        "image/tiff",
        "message/rfc822",
        "application/vnd.ms-outlook",
      ] as const,
      ACCEPTED_EXTENSIONS: [
        ".pdf",
        ".doc",
        ".docx",
        ".xls",
        ".xlsx",
        ".csv",
        ".txt",
        ".png",
        ".jpg",
        ".jpeg",
        ".gif",
        ".tif",
        ".tiff",
        ".eml",
        ".msg",
      ] as const,
      VALIDATION_MESSAGE: "Accepted: PDF, Word, Excel, CSV, text, images (PNG/JPG/GIF/TIFF), or email (EML/MSG)",
      MAX_FILES: 20,
      LABEL: 'Drag & drop a reference document or <span class="filepond--label-action">browse</span>',
    },
  },

  // File field names for API
  DOCUMENTS_FIELD_NAME: "documents",
  USER_ID_FIELD_NAME: "userId",
  CONTAINER_NAME_FIELD_NAME: "containerName",

  // Default values
  DEFAULT_CONTAINER_NAME: "claims-documents",
  DEFAULT_USER_ID: "default-user",
} as const;

export const BLOCKED_EXTENSIONS = [
  ".exe",
  ".bat",
  ".cmd",
  ".sh",
  ".ps1",
  ".msi",
  ".dll",
  ".scr",
  ".com",
  ".js",
  ".vbs",
  ".wsf",
  ".hta",
  ".jar",
  ".py",
  ".rb",
  ".pl",
  ".html",
  ".htm",
  ".php",
  ".asp",
  ".aspx",
  ".jsp",
] as const;

// Type helpers for document types
export type DropzoneDocumentType = keyof typeof FILE_CONSTANTS.DROPZONE_DOCUMENT;

// Type for individual document type configuration
export type DocumentTypeConfig = (typeof FILE_CONSTANTS.DROPZONE_DOCUMENT)[DropzoneDocumentType];

// Helper function to get document type config with proper typing
export const getDocumentTypeConfig = (documentType: DropzoneDocumentType): DocumentTypeConfig => {
  return FILE_CONSTANTS.DROPZONE_DOCUMENT[documentType];
};

// Helper function to get accepted types for a document type
export const getAcceptedTypesForDocument = (documentType: DropzoneDocumentType) => {
  const config = getDocumentTypeConfig(documentType);
  return [...config.ACCEPTED_MIME_TYPES];
};
